import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null });
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "authorized" || data.payment_status === "captured") {
        setState({ status: "done", data });
      } else if (attempts >= 6) {
        setState({ status: "pending", data });
      } else {
        setAttempts((a) => a + 1);
      }
    } catch {
      setState({ status: "error", data: null });
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    if (state.status === "loading" || (state.status !== "done" && attempts > 0 && attempts <= 6)) {
      const t = setTimeout(poll, attempts === 0 ? 0 : 1500);
      return () => clearTimeout(t);
    }
  }, [poll, sessionId, attempts, state.status]);

  return (
    <div className="min-h-screen grain relative flex items-center justify-center bg-[var(--nl-bg)] px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg border border-white/10 bg-[var(--nl-bg-2)] p-10 text-center"
      >
        <Link to="/" className="flex items-baseline gap-1 mb-8 justify-center">
          <span className="font-serif text-2xl">Namarck</span>
          <span className="font-serif text-2xl italic text-gold">Limo</span>
        </Link>

        {state.status === "loading" && <p className="label text-gold animate-pulse py-10" data-testid="payment-loading">Confirming your hold…</p>}

        {state.status === "done" && (
          <>
            <span className="font-serif text-6xl text-gold block mb-6">✓</span>
            <h1 className="font-serif font-light text-4xl mb-3">Card authorized. Ride reserved.</h1>
            <p className="text-white/50 font-body mb-8">
              We've placed a hold of <span className="text-gold">${state.data.amount} CAD</span>{state.data.is_deposit ? " (deposit)" : ""} on your card — you're charged only after your ride is complete. A confirmation is on its way to your email.
            </p>
            <div className="text-left border border-white/10 divide-y divide-white/5 mb-8">
              {[["Vehicle", state.data.vehicle], ["Pickup", state.data.pickup], ["Drop-off", state.data.dropoff], ["When", `${state.data.date} · ${state.data.time}`]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-5 py-3 font-body text-sm">
                  <span className="text-white/40">{k}</span><span className="text-white/80">{v}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {state.status === "pending" && (
          <>
            <h1 className="font-serif font-light text-3xl mb-3">Almost there…</h1>
            <p className="text-white/50 font-body mb-8">Your payment is still processing. If you completed checkout, your hold will appear shortly and we'll email you.</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <h1 className="font-serif font-light text-3xl mb-3">We couldn't confirm this session.</h1>
            <p className="text-white/50 font-body mb-8">If you were charged or held, contact us at admin@namarcklimo.ca and we'll sort it out.</p>
          </>
        )}

        <Link to="/" data-testid="back-home-btn" className="inline-block label bg-gold text-black px-8 py-4 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300">
          Back to Home
        </Link>
        {state.status === "done" && (
          <Link to={`/track?session_id=${sessionId}`} data-testid="track-ride-btn" className="mt-4 block text-center label text-gold border-b border-gold/40 pb-1 w-fit mx-auto hover:border-gold transition-colors duration-300">
            Track your ride →
          </Link>
        )}
      </motion.div>
    </div>
  );
}
